import { Skeleton } from './ui/skeleton';
import { Card } from './ui/card';

/**
 * Page-shaped skeletons. Each variant mirrors the real page's layout (sticky
 * header position, item row geometry, price-breakdown lines, sticky CTA) so
 * the page snap-in on real-content arrival doesn't shift layout — Cumulative
 * Layout Shift stays near zero and the loading state feels purposeful rather
 * than "generic gray wash". Drop-in replacement for the legacy `<div>Loading…
 * </div>` returns in Cart / Checkout / ProductDetail.
 */

function HeaderSkeleton({ title }) {
  return (
    <header
      className="sticky top-0 z-50 bg-primary text-primary-foreground px-4 py-3"
      data-testid="page-skeleton-header"
    >
      <div className="max-w-4xl mx-auto flex items-center gap-3">
        <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20" />
        <span className="text-lg font-bold font-fraunces opacity-90">{title}</span>
      </div>
    </header>
  );
}

function CartItemRowSkeleton() {
  return (
    <Card className="p-3 flex gap-3 items-center">
      <Skeleton className="w-16 h-16 rounded-md flex-shrink-0" />
      <div className="flex-1 space-y-2 min-w-0">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <div className="flex items-center justify-between pt-1">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-4 w-12" />
        </div>
      </div>
    </Card>
  );
}

function PriceBreakdownSkeleton({ rows = 4 }) {
  return (
    <Card className="p-4 space-y-3">
      <Skeleton className="h-5 w-32 mb-2" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex justify-between items-center">
          <Skeleton className="h-3" style={{ width: `${50 + (i * 7) % 25}%` }} />
          <Skeleton className="h-3 w-14" />
        </div>
      ))}
      <div className="pt-3 border-t flex justify-between items-center">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-6 w-20" />
      </div>
    </Card>
  );
}

export function CartSkeleton() {
  return (
    <div className="min-h-[calc(100dvh_-_env(safe-area-inset-bottom))] bg-background pb-24" data-testid="cart-skeleton">
      <HeaderSkeleton title="My Cart" />
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <CartItemRowSkeleton />
            <CartItemRowSkeleton />
            <CartItemRowSkeleton />
          </div>
          <div className="lg:col-span-1">
            <PriceBreakdownSkeleton rows={5} />
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
        <Skeleton className="h-12 w-full rounded-full" />
      </div>
    </div>
  );
}

export function CheckoutSkeleton() {
  return (
    <div className="min-h-[100dvh] bg-background pb-8" data-testid="checkout-skeleton">
      <HeaderSkeleton title="Checkout" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-6 space-y-5">
              <Skeleton className="h-6 w-32" />
              <div className="space-y-3">
                <Skeleton className="h-4 w-24" />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-12 rounded-md" />
                  <Skeleton className="h-12 rounded-md" />
                </div>
              </div>
              <div className="space-y-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-11 w-full rounded-md" />
                <Skeleton className="h-11 w-full rounded-md" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-24 w-full rounded-md" />
              </div>
            </Card>
          </div>
          <div className="lg:col-span-1">
            <PriceBreakdownSkeleton rows={4} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Menu-dependent home sections (Bestsellers + Spotlight + Categories).
 * Renders only while Landing is waiting on `fetchMenuItems` to resolve.
 *
 * Pair with the build-time prerender shell at `scripts/prerender-shell.js`:
 * that shell paints a sparse 3-card placeholder under `<div id="root">`
 * BEFORE React mounts; this skeleton picks up the moment React takes over,
 * extending coverage to Spotlight + Categories so the entire middle of the
 * page never goes blank. Card geometry (grid cols, aspect-square + 2-line
 * text rows, 144px-tall spotlight cards, rounded-2xl category tiles) mirrors
 * the real layout in Landing.js so the snap-in when menu data lands doesn't
 * cause cumulative layout shift.
 */
export function LandingMenuSkeleton() {
  return (
    <>
      <section className="px-4 mb-6" data-testid="landing-skeleton-bestsellers">
        <div className="flex items-center justify-between mb-3">
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-square w-full rounded-none" />
              <div className="p-2.5 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="px-4 mb-6" data-testid="landing-skeleton-spotlight">
        <div className="mb-3 space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="overflow-hidden flex-shrink-0 w-44">
              <Skeleton className="h-36 w-full rounded-none" />
              <div className="p-2.5 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="px-4 mb-6" data-testid="landing-skeleton-categories">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="aspect-square w-full rounded-2xl" />
              <Skeleton className="h-3 w-3/4 mx-auto" />
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/**
 * One Menu list row — mirrors the live MenuListItemClassic geometry (a flex
 * Card with a 112/128px image on the LEFT and the name/meta/price+ADD column on
 * the right). Matches the card the app actually ships today (USE_ZOMATO_MENU_CARDS
 * is false in Menu.js); keeps CLS near zero when the real cards land. Shared by
 * MenuSkeleton (route Suspense fallback) and Menu.js's own in-page loading branch.
 */
export function MenuRowSkeleton() {
  return (
    <Card className="flex overflow-hidden">
      <div className="w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 p-2.5">
        <Skeleton className="w-full h-full rounded-xl" />
      </div>
      <div className="flex-1 py-2.5 pr-3 flex flex-col min-w-0">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/4 mt-2" />
        <div className="mt-auto flex items-center justify-between">
          <Skeleton className="h-5 w-12" />
          <Skeleton className="h-8 w-16 rounded-full" />
        </div>
      </div>
    </Card>
  );
}

/**
 * One Menu sub-category group — the big uppercase header bar (e.g. "COLD COFFEE")
 * + N item rows. The real menu groups items under sub-category <h2> headers, so a
 * flat row list would let those headers shove content down ("bounce") when data
 * lands. Mirroring the group shape keeps the skeleton→data swap shift-free.
 */
export function MenuGroupSkeleton({ rows = 3 }) {
  return (
    <div className="mb-6">
      <Skeleton className="h-6 w-40 mb-3" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <MenuRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Full-page Menu skeleton. Renders the REAL dark bg-primary sticky header (title
 * is static, no data) + section-tab strip + quick-filter strip + grouped rows.
 * Wired as the route-level Suspense fallback for /menu in App.js so the chunk-
 * download window paints this instead of the bare spinner on a white body; Menu's
 * own in-page loading branch (under the same dark header) covers the data-fetch
 * window, so the handoff never flashes white.
 */
export function MenuSkeleton() {
  return (
    <div className="min-h-[100dvh] bg-background pb-20" data-testid="menu-skeleton">
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Back-button placeholder: the real header shows a native:hidden back
              arrow, so on web the title sits inset by this slot. Match it (also
              native:hidden) so the title doesn't jump at the skeleton→page handoff. */}
          <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20 native:hidden" />
          <span className="text-lg font-bold font-fraunces opacity-90">Menu</span>
          <div className="flex items-center gap-3">
            <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20" />
            <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20" />
          </div>
        </div>
        <div className="flex gap-4 px-4 pb-2.5 border-b border-primary-foreground/10">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-16 bg-primary-foreground/20" />
          ))}
        </div>
      </header>
      <div className="flex gap-2 px-4 py-2.5 bg-muted/50 border-b">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-16 rounded-full flex-shrink-0" />
        ))}
      </div>
      <div className="px-4 pt-4">
        <MenuGroupSkeleton rows={3} />
        <MenuGroupSkeleton rows={2} />
      </div>
    </div>
  );
}

/**
 * One offer card — title/subtitle + image-left item row + add button, matching
 * the Today's-Specials / free-coffee cards on OfferOfTheDay. Shared so both the
 * route Suspense fallback (OffersSkeleton) and OfferOfTheDay's in-page loading
 * branch render the same shape.
 */
export function OfferCardSkeleton() {
  return (
    <Card className="p-4 space-y-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-3 w-56" />
      <div className="flex items-center gap-3 pt-1">
        <Skeleton className="w-14 h-14 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
        <Skeleton className="h-8 w-16 rounded-full flex-shrink-0" />
      </div>
    </Card>
  );
}

/**
 * Full-page Offers & Rewards skeleton. Real dark header + 3 OfferCardSkeletons.
 * Route-level Suspense fallback for /offer-of-the-day; OfferOfTheDay's own loading
 * branch renders the same OfferCardSkeletons (data-fetch window).
 * Root min-height = calc(100dvh - safe-area-inset-bottom) so the page + App.js's
 * bottom-nav safe-area wrapper padding sum to exactly one screen — no bounce/scroll
 * on short content (env() is 0 on web/Android, so universal-safe).
 */
export function OffersSkeleton() {
  return (
    <div className="min-h-[calc(100dvh_-_env(safe-area-inset-bottom))] bg-background pb-24" data-testid="offers-skeleton">
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20 native:hidden" />
          <span className="text-base font-bold opacity-90">Offers &amp; Rewards</span>
        </div>
      </header>
      <div className="px-4 py-4 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <OfferCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * One pass card — mirrors the reworked SubscriptionPassCard marketing geometry
 * (2026-06): full-width image banner → name/tagline → "What's included" box →
 * 3-column stat strip → full-width "Get this pass" CTA pill.
 */
function PassCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      {/* image banner */}
      <Skeleton className="h-40 w-full rounded-none" />
      <div className="p-5 space-y-3.5">
        {/* name + tagline */}
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        {/* inclusions box */}
        <div className="rounded-2xl border p-3.5 space-y-2.5">
          <Skeleton className="h-2.5 w-24" />
          <div className="flex flex-wrap gap-1.5">
            <Skeleton className="h-7 w-24 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-7 w-28 rounded-full" />
          </div>
        </div>
        {/* 3-col stat strip */}
        <Skeleton className="h-16 w-full rounded-2xl" />
        {/* CTA */}
        <Skeleton className="h-12 w-full rounded-full" />
      </div>
    </Card>
  );
}

/**
 * Full-page Coffee Passes skeleton. Real dark header (max-w-4xl centered, like
 * the live page) + intro line + 2 pass cards. Route-level Suspense fallback for
 * /subscriptions AND the page's own loading branch (replaces the bare white
 * "Loading…" that page used to show — the one residual white-body screen).
 */
export function PlansSkeleton() {
  return (
    <div className="min-h-[calc(100dvh_-_env(safe-area-inset-bottom))] bg-background pb-24" data-testid="plans-skeleton">
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded bg-primary-foreground/20 native:hidden" />
          <span className="text-lg font-bold font-fraunces opacity-90">Coffee Passes</span>
        </div>
      </header>
      <div className="max-w-4xl mx-auto space-y-4 px-4 pt-5">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-5 w-28 mt-2" />
        <PassCardSkeleton />
        <PassCardSkeleton />
      </div>
    </div>
  );
}

export function ProductDetailSkeleton({ isSheet = false }) {
  const body = (
    <>
      <Skeleton className="w-full aspect-square rounded-none" />
      <div className="p-4 space-y-5">
        <div className="space-y-3">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-5 w-20" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-20 rounded-full" />
            <Skeleton className="h-10 w-20 rounded-full" />
            <Skeleton className="h-10 w-20 rounded-full" />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-5 w-28" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const footer = (
    <div className="border-t p-3 bg-background pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <Skeleton className="h-12 w-full rounded-full" />
    </div>
  );

  if (isSheet) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden" data-testid="product-detail-skeleton">
        <div className="flex-1 overflow-y-auto">{body}</div>
        {footer}
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background pb-20" data-testid="product-detail-skeleton">
      {body}
      <div className="fixed bottom-0 left-0 right-0 z-40">{footer}</div>
    </div>
  );
}
